import React, { useState, useEffect } from 'react';
import { useParams, Outlet, useNavigate, useLocation } from 'react-router-dom';
import ShelterOverview from './ShelterOverview';
import ShelterAnimals from './ShelterAnimals';
import ShelterContact from './ShelterContact';
import ShelterDonations from './ShelterDonations';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Avatar,
  Chip,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Home as HomeIcon,
  Pets as PetsIcon,
  ContactPhone as ContactIcon,
  Favorite as DonationsIcon
} from '@mui/icons-material';
import { getShelterById } from '@services/sheltersService';

interface Shelter {
  id: string;
  name: string;
  description?: string;
  city: string;
  county: string;
  email?: string;
  phone?: string;
  website?: string;
  logo?: string;
  animal_count?: number;
  status: string;
  created_at: string;
}

const ShelterLayout: React.FC = () => {
  const { shelterId } = useParams<{ shelterId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [shelter, setShelter] = useState<Shelter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determină tab-ul activ bazat pe URL
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.includes('/animals')) return 1;
    if (path.includes('/contact')) return 2;
    if (path.includes('/donations')) return 3;
    return 0; // default: prezentare
  };

  const [activeTab, setActiveTab] = useState(getCurrentTab());

  useEffect(() => {
    const fetchShelter = async () => {
      if (!shelterId) return;
      
      try {
        setLoading(true);
        const response = await getShelterById(shelterId);
        setShelter(response.data);
      } catch (err: any) {
        setError(err.message || 'Eroare la încărcarea adapostului');
      } finally {
        setLoading(false);
      }
    };

    fetchShelter();
  }, [shelterId]);

  useEffect(() => {
    setActiveTab(getCurrentTab());
  }, [location.pathname]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const basePath = `/client/shelters/${shelterId}`;
    
    switch (newValue) {
      case 0:
        navigate(basePath);
        break;
      case 1:
        navigate(`${basePath}/animals`);
        break;
      case 2:
        navigate(`${basePath}/contact`);
        break;
      case 3:
        navigate(`${basePath}/donations`);
        break;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !shelter) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error || 'Adapostul nu a fost găsit'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          href="/client/shelters" 
          onClick={(e) => {
            e.preventDefault();
            navigate('/client/shelters');
          }}
        >
          Adaposturi & Adoptii
        </Link>
        <Typography color="text.primary">{shelter.name}</Typography>
      </Breadcrumbs>

      {/* Header cu informații adapost */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={3}>
          <Avatar
            src={shelter.logo}
            sx={{ width: 80, height: 80 }}
          >
            {shelter.name.charAt(0)}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h4" gutterBottom>
              {shelter.name}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {shelter.city}, {shelter.county}
            </Typography>
            {shelter.description && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                {shelter.description}
              </Typography>
            )}
            <Box display="flex" gap={1} alignItems="center">
              <Chip 
                label={`${shelter.animal_count || 0} animale disponibile`}
                color="primary"
                size="small"
              />
              <Chip 
                label={shelter.status === 'active' ? 'Activ' : 'Inactiv'}
                color={shelter.status === 'active' ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<HomeIcon />} 
            label="Prezentare" 
            iconPosition="start"
          />
          <Tab 
            icon={<PetsIcon />} 
            label={`Animale (${shelter.animal_count || 0})`}
            iconPosition="start"
          />
          <Tab 
            icon={<ContactIcon />} 
            label="Contact" 
            iconPosition="start"
          />
          <Tab 
            icon={<DonationsIcon />} 
            label="Donatii" 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Content */}
      {(() => {
        const path = location.pathname;
        if (path.includes('/animals')) {
          return <ShelterAnimals shelter={shelter} />;
        }
        if (path.includes('/contact')) {
          return <ShelterContact shelter={shelter} />;
        }
        if (path.includes('/donations')) {
          return <ShelterDonations shelter={shelter} />;
        }
        return <ShelterOverview shelter={shelter} />;
      })()}
    </Container>
  );
};

export default ShelterLayout;
